import React, { useState, useMemo, useEffect } from 'react';
import { Modal, Input, message } from 'antd';
import PropTypes from 'prop-types';
import { TapdUtil } from 'utils/tapd-util';
import RestHelper from 'utils/RestHelper';
import { connect } from 'react-redux';
import { isNullish } from 'utils/nullish';
import { ze } from 'utils/zhEn';
import { ix } from 'utils/i18n-with-context';
import { CrashUtil } from 'utils/api/crash';
import { getIssueDetailUrlPrefix } from 'utils/helper';
import { getReportCategoryByExceptionTypeInt } from 'utils/constants/exception-type-int';
import { useTranslation } from 'react-i18next';
import { Base64 } from 'utils/base64';

/** 和平定制需求，选择多条上报添加到tapd评论区 */
const TapdAddCommentModal = ({
  reduxState,
  visible,
  initTapdUrl,
  onOk,
  onCancel,
  selectedReports = [],
}) => {
  const { t } = useTranslation();

  const currentApp = useMemo(() => reduxState.app.get('current').toJS(), [reduxState.app.get('current')]);
  const { appId, pid } = currentApp;

  const currentUser = reduxState.user.get('current').toJS();
  const { nickname: userNickname } = currentUser;

  const [inputValue, setInputValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setInputValue(initTapdUrl);
  }, [initTapdUrl]);

  async function addCommentToTapd(tapdUrl, selectedReports) {
    const bugId = TapdUtil.getBugIdFromUrl(tapdUrl);
    if (!bugId) {
      message.error('请输入正确的TAPD缺陷单地址');
      return false;
    }

    if (!selectedReports || selectedReports.length === 0) {
      message.error('请先选择要追加的上报');
      return false;
    }

    try {
      setIsSubmitting(true);

      let successCount = 0;
      let failCount = 0;

      // 遍历每个report，逐个调用接口
      for (let i = 0; i < selectedReports.length; i++) {
        const report = selectedReports[i];

        try {
          // 使用makeDescription函数生成description
          let description = makeDescription(report);
          description = description.replaceAll('\n', '<br>');

          // 使用Base64.encodeAsString编码description
          const commentDescriptionBase64 = Base64.encodeAsString(description);

          // 调用API添加评论到TAPD
          const rsp = await RestHelper.post('/redir/query/data/tapdAddComment', {
            appId,
            bugId,
            commentDescriptionBase64,
            commentAuthor: userNickname,
          });

          successCount++;
        } catch (error) {
          failCount++;
          console.error(`Error adding comment for report ${report.crashId}:`, error);
        }
      }

      if (successCount > 0 && failCount === 0) {
        message.success(`成功将${successCount}条上报信息追加到TAPD单评论区`);
        return true;
      } else if (successCount > 0 && failCount > 0) {
        message.warning(`成功追加${successCount}条，失败${failCount}条上报信息`);
        return true; // 部分成功也算成功
      } else {
        message.error('所有上报信息追加失败');
        return false;
      }
    } catch (error) {
      message.error('追加评论失败: ' + error.message);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleOk() {
    const tapdUrl = inputValue.trim();
    if (!tapdUrl) {
      message.error('请输入TAPD缺陷单地址');
      return;
    }

    const success = await addCommentToTapd(tapdUrl, selectedReports);
    if (success) {
      onOk(tapdUrl);
      setInputValue(''); // 清空输入框
    }
  }

  function handleCancel() {
    onCancel();
    setInputValue(''); // 清空输入框
  }

  function handleInputChange(e) {
    setInputValue(e.target.value);
  }

  // 参考TapdModal组件里面生成description的方式
  function makeDescription(crashMap) {
    if (!crashMap) {
      return '';
    }

    const detailInfo = CrashUtil.makeRefinedCrashMap(crashMap);
    const {
      retraceCrashDetail,
      crashId,
      issueId,
      crashExceptionType,
    } = detailInfo;
    const issueExceptionType = crashExceptionType;
    const platformId = pid;

    const issueDetailUrlPrefix = getIssueDetailUrlPrefix(getReportCategoryByExceptionTypeInt(issueExceptionType));
    const crashIdUrlSuffix = crashId ? `/report-id/${crashId}` : '';
    const issueUrl = `${window.location.origin}/${issueDetailUrlPrefix}/${appId}/${issueId}${crashIdUrlSuffix}?pid=${platformId}`;

    const crashStackLines = `<pre>${String(retraceCrashDetail || '')}</pre>`;

    let description =
      `Issue ID: ${issueId}

${ze('异常详情地址', 'Web URL')}: <a href='${issueUrl}'>${issueUrl}</a>

${t('REPORTDETAIL.异常进程')}#${t('REPORTDETAIL.异常线程')}: ${detailInfo.processName || ''}#${detailInfo.threadName || ''}

${t('REPORTDETAIL.expMessage')}：${detailInfo.expMessage || ''}

${t('issueCrashFilterKey.crashDetail')}:
${crashStackLines}

${t('issueCrashFilterKey.crashTime')}: ${detailInfo.crashTimeFormatted}
${t('issueCrashFilterKey.uploadTime')}: ${detailInfo.uploadTimeFormatted}
${t('REPORTDETAIL.apkName')}: ${detailInfo.bundleId}
${t('REPORTDETAIL.version')}: ${detailInfo.productVersion}
${t('REPORTDETAIL.hardware')}: ${detailInfo.hardware}
${t('REPORTDETAIL.osVersion')}: ${detailInfo.osVersion}
`;
    return description;
  }

  return (
    <Modal
      width={800}
      maskClosable={false}
      title={`将选中的${selectedReports.length}条上报追加到TAPD单评论区`}
      visible={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      okText="确认"
      cancelText="取消"
      destroyOnClose={true}
      confirmLoading={isSubmitting}
      okButtonProps={{ disabled: !inputValue || isSubmitting }}
    >
      { visible && <div>
        <div>输入TAPD缺陷单地址</div>
        <div style={{ marginTop: '8px' }}>
          <Input
            style={{ width: '100%' }}
            value={inputValue}
            onChange={handleInputChange}
            placeholder="请输入TAPD缺陷单URL"
            onPressEnter={handleOk}
            disabled={isSubmitting}
          />
        </div>
        {selectedReports.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>将要追加的上报信息预览：</div>
            <div style={{
              maxHeight: '200px',
              overflow: 'auto',
              border: '1px solid #d9d9d9',
              padding: '8px',
              whiteSpace: 'pre-wrap',
              backgroundColor: '#fafafa',
              fontSize: '12px'
            }}>
              { makeDescription(selectedReports[0]) }
            </div>
            { selectedReports.length > 1 && (
              <div style={{ color: '#666', fontStyle: 'italic' }}>
                ...还有{selectedReports.length - 1}条上报
              </div>
            ) }
          </div>
        )}
      </div> }
    </Modal>
  );
};

TapdAddCommentModal.propTypes = {
  reduxState: PropTypes.object.isRequired,
  visible: PropTypes.bool.isRequired,
  onOk: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  selectedReports: PropTypes.array,
};

const mapStateToProps = (state) => ({
  reduxState: state,
});

export default connect(mapStateToProps)(TapdAddCommentModal);
